import { motion } from "framer-motion";
import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { TiltCard } from "@/components/equinox/MotionEnhancers";
import { PremiumStatusBadge } from "@/components/equinox/PremiumStatusBadge";
import { systems } from "@/components/equinox/data";
import { getServiceDetailBySlug } from "@/components/equinox/serviceDetailsData";

const featuredCardClasses = [
  "lg:col-span-7",
  "lg:col-span-5",
  "lg:col-span-5",
  "lg:col-span-7",
  "lg:col-span-7",
  "lg:col-span-5",
];

export default function SystemsPage() {
  return (
    <section className="px-6 pb-20 pt-16 sm:px-8 lg:px-12 lg:pb-24 lg:pt-20">
      <div className="mx-auto max-w-7xl space-y-10">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="noise-surface glitter-card glass-panel overflow-hidden rounded-[2.6rem] p-6 lg:p-9" data-testid="services-page-hero-shell">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-end">
            <div className="max-w-3xl">
              <p className="section-kicker">Services</p>
              <h1 className="section-heading mt-5 text-white">Growth systems, built like premium operating infrastructure.</h1>
              <p className="section-copy mt-6 text-white/[0.64]">
                Each Equinox system is designed like a high-value operating layer: clear scope, operator-led execution, sharper outcomes, and a more credible route to scale.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="services-page-hero-badges">
                <PremiumStatusBadge label="Operator-led execution" variant="live" />
                <PremiumStatusBadge label="Luxury growth systems" variant="benchmark" />
                <PremiumStatusBadge label="For apps + brands" variant="estimated" />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3" data-testid="services-page-hero-metrics">
              {[
                { label: "Approach", value: "System-first", helper: "not generic service packaging" },
                { label: "Execution", value: "Human-led", helper: "operators, creators, review teams, media buyers" },
                { label: "Coverage", value: "Online + field", helper: "growth, trust, PR, and select offline activation" },
              ].map((item, index) => (
                <div key={item.label} className="control-room-panel rounded-[1.6rem] p-5" data-testid={`services-page-hero-metric-${index + 1}`}>
                  <p className="text-[11px] uppercase tracking-[0.22em] text-white/[0.38]">{item.label}</p>
                  <p className="mt-3 font-display text-3xl text-white">{item.value}</p>
                  <p className="mt-3 text-sm leading-7 text-white/[0.62]">{item.helper}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="grid gap-6 lg:grid-cols-12" data-testid="services-page-grid">
          {systems.map((detail, index) => {
            const Icon = detail.icon;
            const detailPage = getServiceDetailBySlug(detail.slug);
            const hasOfflineCoverage = Boolean(detailPage?.fieldCoverage);

            return (
              <Link key={detail.title} to={`/services/${detail.slug}`} className={`block ${featuredCardClasses[index] || "lg:col-span-6"}`} data-testid={`systems-page-card-link-${index + 1}`}>
                <TiltCard
                  className="noise-surface glitter-card glass-panel cinematic-card h-full rounded-[2.2rem] p-6 lg:p-7"
                  testId={`systems-page-card-${index + 1}`}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.55, delay: index * 0.06 }}
                    className="flex h-full flex-col"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-[1.6rem] border border-white/[0.12] bg-gradient-to-br from-blue-500/20 via-white/[0.05] to-amber-200/10 text-white shadow-[0_0_30px_rgba(37,99,235,0.14)]">
                        <Icon className="h-7 w-7" />
                      </div>
                      <div className="flex flex-wrap justify-end gap-2">
                        <PremiumStatusBadge label="Premium system" variant="benchmark" testId={`systems-page-card-status-${index + 1}`} />
                        <PremiumStatusBadge label={detail.outcome} variant="live" testId={`systems-page-card-outcome-${index + 1}`} />
                      </div>
                    </div>
                    <h2 className="font-display text-3xl text-white">{detail.title}</h2>
                    <p className="mt-4 text-sm leading-7 text-white/[0.65]">{detail.description}</p>
                    <div className="mt-7 grid gap-4">
                      {[
                        { label: "What it does", value: detail.what },
                        { label: "How Equinox is different", value: detail.different },
                      ].map((block) => (
                        <div key={block.label} className="control-room-panel rounded-[1.25rem] p-4">
                          <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">{block.label}</p>
                          <p className="mt-3 text-sm leading-7 text-white/[0.72]">{block.value}</p>
                        </div>
                      ))}
                      {hasOfflineCoverage ? (
                        <div className="offline-capability-panel rounded-[1.35rem] p-5" data-testid={`systems-page-offline-marketing-panel-${index + 1}`}>
                          <div className="flex items-center gap-2 text-blue-100">
                            <MapPin className="h-4 w-4" />
                            <p className="text-[11px] uppercase tracking-[0.22em]">Offline marketing availability</p>
                          </div>
                          <p className="mt-4 max-w-2xl text-sm leading-7 text-white/[0.68]">
                            Available for offline marketing, PR coordination, and field activation in the following markets right now.
                          </p>
                          <div className="mt-4 flex flex-wrap gap-2" data-testid={`systems-page-offline-marketing-cities-${index + 1}`}>
                            {detailPage.fieldCoverage.locations.map((location) => (
                              <span key={location} className="rounded-full border border-white/[0.1] bg-black/25 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-white/[0.68]">
                                {location}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                      <div className="control-room-panel rounded-[1.25rem] p-4">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs uppercase tracking-[0.2em] text-white/[0.4]">Expected outcomes</p>
                          <PremiumStatusBadge label="Outcome-focused" variant="estimated" />
                        </div>
                        <div className="mt-4 space-y-3">
                          {detail.outcomes.map((bullet) => (
                            <div key={bullet} className="rounded-[1.1rem] border border-white/[0.08] bg-black/20 px-4 py-3 text-sm text-white/[0.72]">
                              <div className="flex items-center justify-between gap-3">
                              <span>{bullet}</span>
                              <ArrowRight className="h-4 w-4 text-blue-200" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-auto pt-6">
                      <div className="inline-flex items-center gap-2 text-sm font-medium text-blue-100" data-testid={`systems-page-card-cta-${index + 1}`}>
                        View full system details
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </motion.div>
                </TiltCard>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}